import { Fragment, useState } from 'react';

import { ActionIcon, ActionIconProps, Menu, MenuProps } from '@mantine/core';
import { IconMore } from 'ui-icons';

const KebabMenuItemList = ({ items }: { items: KebabMenuItem[] }) => {
  return (
    <>
      {items.map((item, index) => (
        <Fragment key={index}>
          {item.withDivider && <Menu.Divider />}
          <Menu.Item
            leftSection={item.icon}
            onClick={(e) => {
              e.stopPropagation();
              return item.onClick?.(item?.data);
            }}
            variant={item.variant}
            disabled={item.disabled}
          >
            {item.label}
          </Menu.Item>
        </Fragment>
      ))}
    </>
  );
};

type KebabMenuItemVariants = 'destructive';
interface KebabMenuItem<TData = Record<string, unknown>> {
  label: string;
  icon?: React.ReactNode;
  onClick: (item?: TData) => void;
  visible?: boolean;
  variant?: KebabMenuItemVariants;
  data?: TData;
  /**
   * If true, will add a divider above the menu item
   */
  withDivider?: boolean;
  disabled?: boolean;
}
interface KebabMenuProps<TData = Record<string, unknown>> extends MenuProps {
  items?: KebabMenuItem<TData>[];
  children?: React.ReactNode;
  buttonProps?: ActionIconProps;
  loading?: boolean;
  target?: React.ReactNode;
}

const KebabMenu = ({
  items,
  children,
  buttonProps,
  target,
  loading = false,
  ...props
}: KebabMenuProps) => {
  const { size, variant } = {
    size: buttonProps?.size ?? 'lg',
    variant: buttonProps?.variant ?? 'subtle',
  };
  const [isOpened, setIsOpened] = useState(false);

  const itemList = items?.filter((item) => item.visible !== false) ?? [];
  if (!itemList?.length && !children) return null;

  return (
    <Menu shadow="md" position="bottom-end" {...props} onChange={() => setIsOpened(!isOpened)}>
      <Menu.Target>
        {target ?? (
          <ActionIcon
            {...buttonProps}
            size={size}
            variant={variant}
            onClick={(e) => {
              e.stopPropagation();
            }}
            loading={loading}
            aria-label="more options"
          >
            <IconMore />
          </ActionIcon>
        )}
      </Menu.Target>
      <Menu.Dropdown miw={220}>
        {items ? <KebabMenuItemList items={itemList} /> : children}
      </Menu.Dropdown>
    </Menu>
  );
};

const createItems = <TData extends Record<string, unknown>>(items: KebabMenuItem<TData>[]) =>
  items as KebabMenuItem[];

KebabMenu.createItems = createItems;

export { KebabMenu, KebabMenuItemList };
export type { KebabMenuItem, KebabMenuProps };
